import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { ButtonPassThrough } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';

import type { FeatureAnnouncement } from '@customer-portal/data-access/feature-announcements';
import {
  SharedButtonComponent,
  SharedButtonType,
} from '@customer-portal/shared/components/button';

@Component({
  selector: 'lib-sidebar-announcement-popover',
  imports: [CommonModule, PopoverModule, SharedButtonComponent],
  templateUrl: './sidebar-announcement-popover.component.html',
  styleUrl: './sidebar-announcement-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarAnnouncementPopoverComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly trigger =
    viewChild<ElementRef<HTMLButtonElement>>('trigger');
  readonly announcement = input.required<FeatureAnnouncement>();
  readonly triggerLabel = input<string | undefined>();
  readonly ctaLabel = input<string | undefined>();
  readonly feedback = input(false);
  readonly expanded = input(false);
  readonly seen = input(false);
  readonly disabled = input(false);
  readonly openLabel = input<string | null | undefined>();
  readonly opened = output<Popover>();
  readonly closed = output<{
    popover: Popover;
    trigger: HTMLButtonElement | null;
  }>();
  readonly cta = output<Popover>();
  readonly openFeature = output<Popover>();
  readonly sharedButtonType = SharedButtonType;
  readonly actionButtonPT: ButtonPassThrough = {
    root: {
      style: {
        whiteSpace: 'nowrap',
      },
    },
  };

  get primaryLabel(): string | null {
    const announcement = this.announcement();

    if (!announcement.hasTour) {
      return null;
    }

    return this.translate('whatsNew.learnMore');
  }

  get resolvedTriggerLabel(): string {
    if (this.triggerLabel()) {
      return this.triggerLabel() as string;
    }

    const feature = this.announcement();
    const title = this.translate(feature.sidebarTitle ?? feature.title);
    const state = this.seen()
      ? ''
      : `, ${this.transloco.translate('whatsNew.new')}`;

    return `${this.transloco.translate('whatsNew.title')}: ${title}${state}`;
  }

  get resolvedOpenLabel(): string | null {
    const configuredLabel = this.openLabel();

    if (configuredLabel !== undefined) {
      return configuredLabel;
    }

    const feature = this.announcement();

    return feature.hasTour && feature.route
      ? this.transloco.translate('whatsNew.viewDetails')
      : null;
  }

  translate(key: string): string {
    return this.transloco.translate(key);
  }

  onShow(popover: Popover): void {
    this.opened.emit(popover);
  }

  onHide(popover: Popover): void {
    this.closed.emit({
      popover,
      trigger: this.trigger()?.nativeElement ?? null,
    });
  }
}
